import { clsx, type ClassValue } from "clsx"
import { twMerge } from "tailwind-merge"
import { useState } from "react"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

// Marker for any panel that should be treated as a child of an open modal —
// shadcn Dialog/Sheet check this in their outside-interaction handlers so
// clicks landing in the panel don't dismiss the parent.
export const RIGHT_SIDEBAR_ATTR = 'data-right-sidebar'

// Visible-RightSidebar registry. RightSidebar registers while visible;
// Dialogs sample it at open time to stack above the sidebar's z-[55]/z-[60]
// (sidebars opened FROM a dialog still layer above it, since that dialog
// opened when no sidebar was visible).
let visibleRightSidebars = 0

/** Register a sidebar as visible. Returns its stacking depth (0 for the first
 * one open) plus a deregister callback, so a sidebar opened on top of another
 * — a picker's info panel, say — layers above its parent instead of behind it.
 * Every RightSidebar shares one z-index, so without a depth the later one in
 * DOM order wins, which for app-root-mounted panels is the wrong one. */
export function registerVisibleRightSidebar(): {
  depth: number
  release: () => void
} {
  const depth = visibleRightSidebars
  visibleRightSidebars += 1
  return {
    depth,
    release: () => {
      visibleRightSidebars -= 1
    },
  }
}

export function hasVisibleRightSidebar(): boolean {
  return visibleRightSidebars > 0
}

/** Whether a RightSidebar was visible when the calling component mounted.
 * Call it from a layer the dialog's portal mounts on open (overlay, content
 * box), never from a wrapper rendered while the dialog is still closed: that
 * one mounts with its owner, before the owner's side panel registers.
 * Output: true when the layer must stack above the sidebar (z-[70]). */
export function useOverSidebar(): boolean {
  const [overSidebar] = useState(hasVisibleRightSidebar)
  return overSidebar
}

// Radix's outside-interaction events are CustomEvents whose top-level
// `target` is the document; the actually-clicked element is on
// `detail.originalEvent.target`.
export function isInsideRightSidebar(
  e: { target: EventTarget | null; detail?: { originalEvent?: Event } },
): boolean {
  const original = e.detail?.originalEvent?.target ?? e.target
  if (!(original instanceof Element)) return false
  return !!original.closest(`[${RIGHT_SIDEBAR_ATTR}]`)
}
